import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { Customer } from '../models/pre-simulation';
import { CustomerMgr } from 'src/app/modules/customermgr/models/customermgr';

@Injectable({
  providedIn: 'root'
})
export class PreSimulationService {
  private formValidity = new BehaviorSubject<boolean>(false);
  private customers = new BehaviorSubject<Customer[] | CustomerMgr[] | null>(null);
  private countries = new BehaviorSubject<any[] | null>(null);
  private customerCreated = new BehaviorSubject<Customer | null>(null);
  private countryDescription = new BehaviorSubject<string | null>(null);
  private customerDescription = new BehaviorSubject<string | null>(null);
  private currentCustomerId = new BehaviorSubject<number | null>(null);
  private currentCountryId = new BehaviorSubject<number | null>(null);
  private loadedCustomerId = new BehaviorSubject<number | null>(null);
  private loadedCountryId = new BehaviorSubject<number | null>(null);
  private savedCustomerId = new BehaviorSubject<number | null>(null);
  private savedCountryId = new BehaviorSubject<number | null>(null);
  private loadedFormValidity = new BehaviorSubject<boolean | null>(null);

  formValidity$ = this.formValidity.asObservable();
  customers$ = this.customers.asObservable();
  countries$ = this.countries.asObservable();
  customerCreated$ = this.customerCreated.asObservable();
  countryDescriptionChanges$ = this.countryDescription.asObservable();
  customerDescriptionChanges$ = this.customerDescription.asObservable();
  customerIdChanges$ = this.loadedCustomerId.asObservable();
  countryIdChanges$ = this.loadedCountryId.asObservable();

  updateFormValidity(isValid: boolean, form?: UntypedFormGroup) {
    this.formValidity.next(isValid);
    if (form && form.get('test') && form.get('test').value === true) {
      this.loadedFormValidity.next(false);
    } else {
      this.loadedFormValidity.next(isValid);
    }
  }

  lastForm: UntypedFormGroup | null = null;
  updateForm(form: UntypedFormGroup) {
    this.lastForm = form;
    this.currentCountryId.next(form.get('country').value?.id || null);
    this.currentCustomerId.next(form.get('customer').value?.id || null);
    this.countryDescription.next(form.get('country').value?.description || null);
    this.customerDescription.next(form.get('customer').value?.description || null);
    this.loadedFormValidity.next(form.valid);
  }

  createCustomer(customer: Customer) {
    this.customerCreated.next(customer);
  }

  resetValues() {
    this.formValidity.next(false);
    this.customerCreated.next(null);
    this.customers.next(null);
    this.countryDescription.next(null);
    this.customerDescription.next(null);
    this.currentCustomerId.next(null);
    this.currentCountryId.next(null);
    this.loadedCustomerId.next(null);
    this.loadedCountryId.next(null);
    this.savedCustomerId.next(null);
    this.savedCountryId.next(null);
    this.loadedFormValidity.next(null);
  }

  getCurrentCustomerId(): number | null {
    return this.currentCustomerId.getValue();
  }

  getCurrentCountryId(): number | null {
    return this.currentCountryId.getValue();
  }

  getFormLoadedValidity(): boolean | null {
    if (this.lastForm && this.lastForm.get('test') && this.lastForm.get('test').value === true) {
      return false;
    }
    return this.loadedFormValidity.getValue();
  }

  setSavedCustomerId(id: number | null) {
    this.savedCustomerId.next(id);
  }

  setSavedCountryId(id: number | null) {
    this.savedCountryId.next(id);
  }

  setLoadedCustomerId(id: number) {
    this.loadedCustomerId.next(id);
  }

  setLoadedCountryId(id: number) {
    this.loadedCountryId.next(id);
  }

  setCustomers(customers: Customer[] | CustomerMgr[]) {
    this.customers.next([...customers]);
  }

  getSavedCustomerId(): number | null {
    return this.savedCustomerId.getValue();
  }

  getSavedCountryId(): number | null {
    return this.savedCountryId.getValue();
  }

  hasCustomerAndCountrySelected(): boolean {
    if (!this.lastForm) return false;
    const customer = this.lastForm.get('customer')?.value;
    const country = this.lastForm.get('country')?.value;
    return !!customer && !!country;
  }

  setTestMode(value = true): void {
    if (this.lastForm) {
      const testCtrl = this.lastForm.get('test');
      if (testCtrl) testCtrl.setValue(value);
    }
  }

  setCountries(countries: any[]) {
    this.countries.next(countries);
  }

  getCountries(): any[] | null {
    return this.countries.getValue();
  }
}
